import * as React from "react";
import { createRoot } from "react-dom/client";
import App from "../powerpoint/components/App";
import ExcelApp from "../excel/ExcelApp";
import { TooltipProvider } from "@finster/ui";
import { ThemeProvider } from "../shared/ThemeContext";
import { markOfficeJsUnavailable } from "../powerpoint/services/featureFlagService";
import { bootstrapAnalytics } from "../shared/analytics/client";
import {
  ensureFactSetHostContext,
  getCachedFactSetHostContext,
  initFactSetHostContextListener,
  onFactSetHostContextResolved,
  type FactSetHostContext,
} from "../shared/factset/hostContextService";
import type { OfficeHost } from "../shared/office/officeHost";
import {
  addImageFromBase64ViaIntents,
  addSlideTagsViaIntents,
  applyModificationsViaIntents,
  deleteSlidesViaIntents,
  getSelectedShapesViaIntents,
  exportPresentationAsBase64ViaIntents,
  exportSlideAsImageViaIntents,
  extractPresentationContentViaIntents,
  insertSlidesFromBase64ViaIntents,
  listSlidesViaIntents,
  getSelectedSlideRefViaIntents,
  handleBatchPptOperations,
  isFactSetIntentBridgeAvailable,
  streamSlideThumbnailsViaIntents,
} from "../powerpoint/services/factset/intentBridgeService";
import { registerTaskpaneHostServices } from "./registerHostServices";
import {
  selectFactSetTaskpaneHost,
  TaskpaneHostRouter,
} from "./TaskpaneHostRouter";
import { detectFactSetHostContextWithRetries } from "./factSetHostDetection";
import "../shared/globals.css";

/* global document, Office, Excel, PowerPoint, module, require, HTMLElement, window */

const rootElement: HTMLElement | null = document.getElementById("container");
const root = rootElement ? createRoot(rootElement) : undefined;

declare global {
  interface Window {
    __officeJsReady?: boolean;
    __finsterBoot?: () => void;
  }
}

type FinsterFactSetBridge = {
  version: 1;
  isAvailable: () => boolean;
  getSelectedSlideRef: typeof getSelectedSlideRefViaIntents;
  getSelectedShapes: typeof getSelectedShapesViaIntents;
  listSlides: typeof listSlidesViaIntents;
  extractPresentationContent: typeof extractPresentationContentViaIntents;
  exportSlideAsImage: typeof exportSlideAsImageViaIntents;
  exportPresentationAsBase64: typeof exportPresentationAsBase64ViaIntents;
  streamSlideThumbnails: typeof streamSlideThumbnailsViaIntents;
  insertSlidesFromBase64: typeof insertSlidesFromBase64ViaIntents;
  deleteSlides: typeof deleteSlidesViaIntents;
  addImageFromBase64: typeof addImageFromBase64ViaIntents;
  addSlideTags: typeof addSlideTagsViaIntents;
  applyModifications: typeof applyModificationsViaIntents;
  handleBatchPptOperations: typeof handleBatchPptOperations;
};

function installFinsterFactSetBridge(): void {
  if (typeof window === "undefined") return;

  const bridgeWindow = window as Window & { __finsterFactSetBridge?: FinsterFactSetBridge };

  if (bridgeWindow.__finsterFactSetBridge) return;
  bridgeWindow.__finsterFactSetBridge = {
    version: 1,
    isAvailable: isFactSetIntentBridgeAvailable,
    getSelectedSlideRef: getSelectedSlideRefViaIntents,
    getSelectedShapes: getSelectedShapesViaIntents,
    listSlides: listSlidesViaIntents,
    extractPresentationContent: extractPresentationContentViaIntents,
    exportSlideAsImage: exportSlideAsImageViaIntents,
    exportPresentationAsBase64: exportPresentationAsBase64ViaIntents,
    streamSlideThumbnails: streamSlideThumbnailsViaIntents,
    insertSlidesFromBase64: insertSlidesFromBase64ViaIntents,
    deleteSlides: deleteSlidesViaIntents,
    addImageFromBase64: addImageFromBase64ViaIntents,
    addSlideTags: addSlideTagsViaIntents,
    applyModifications: applyModificationsViaIntents,
    handleBatchPptOperations: handleBatchPptOperations,
  };
}

installFinsterFactSetBridge();
registerTaskpaneHostServices();

function renderApp(
  host: OfficeHost | null,
  onRetryHostDetection?: () => void
) {
  root?.render(
    <ThemeProvider>
      <TooltipProvider>
        <TaskpaneHostRouter
          host={host}
          hostDetectionFailed={Boolean(onRetryHostDetection)}
          onRetryHostDetection={onRetryHostDetection}
          excelApp={<ExcelApp />}
          powerPointApp={<App />}
        />
      </TooltipProvider>
    </ThemeProvider>
  );
}

let booted = false;

function renderResolvedNonOfficeHost(
  host: OfficeHost,
  hostContext: FactSetHostContext | null
): void {
  const isExcel = host === "excel";
  window.__FINSTER_HOST_INFO__ = {
    host: hostContext ? `factset-${host}` : "none",
    hostTypeExcel: "n/a",
    typeofExcel: typeof Excel,
    typeofPowerPoint: typeof PowerPoint,
    isExcel,
    platform: hostContext?.platformType ?? "standalone",
    officeJsAvailable: false,
  };
  console.log(`[Finster] Non-Office host resolved: ${host}`, window.__FINSTER_HOST_INFO__);
  renderApp(host);
}

function renderFactSetTaskpane(): void {
  const bridgeAvailable = isFactSetIntentBridgeAvailable();
  const initialContext = getCachedFactSetHostContext();
  const initialHost = selectFactSetTaskpaneHost(
    initialContext?.appType ?? null,
    bridgeAvailable
  );
  if (initialHost) {
    renderResolvedNonOfficeHost(initialHost, initialContext);
    return;
  }

  // In an embedded FactSet pane, an unknown host is genuinely unresolved.
  // Keep both host apps unmounted so a slow handshake cannot expose
  // PowerPoint onboarding or services inside Excel.
  renderApp(null);

  let resolved = false;
  let unsubscribe = () => {};
  const resolveHost = (hostContext: FactSetHostContext | null): void => {
    const host = selectFactSetTaskpaneHost(
      hostContext?.appType ?? null,
      bridgeAvailable
    );
    if (!host || resolved) return;
    resolved = true;
    unsubscribe();
    renderResolvedNonOfficeHost(host, hostContext);
  };

  unsubscribe = onFactSetHostContextResolved(resolveHost);

  // Close the registration/cache-read race: onConnected may have arrived
  // after the initial read but immediately before the listener was installed.
  resolveHost(getCachedFactSetHostContext());

  const detectHost = async (): Promise<void> => {
    if (resolved) return;
    renderApp(null);

    const hostContext = await detectFactSetHostContextWithRetries({
      detect: (forceRefresh) =>
        ensureFactSetHostContext({
          forceRefresh,
        }),
      shouldStop: () => resolved,
      onError: (error) => {
        console.warn("[Finster] FactSet host context lookup failed:", error);
      },
    });

    if (resolved) return;
    if (hostContext) {
      resolveHost(hostContext);
      return;
    }

    renderApp(null, () => {
      void detectHost();
    });
  };

  if (!resolved) void detectHost();
}

async function boot() {
  if (booted) return;
  booted = true;

  void bootstrapAnalytics();
  initFactSetHostContextListener();

  const officeJsAvailable = typeof Office !== "undefined" && typeof Office.onReady === "function";

  if (officeJsAvailable) {
    let onReadyResolved = false;

    Office.onReady((info) => {
      if (onReadyResolved) return;
      onReadyResolved = true;

      const hasHost = info.host !== null && info.host !== undefined;
      const isExcel = info.host === Office.HostType.Excel;

      if (!hasHost) {
        markOfficeJsUnavailable();
        renderFactSetTaskpane();
        return;
      }

      window.__FINSTER_HOST_INFO__ = {
        host: String(info.host),
        hostTypeExcel: String(Office.HostType.Excel),
        typeofExcel: typeof Excel,
        typeofPowerPoint: typeof PowerPoint,
        isExcel: isExcel,
        platform: hasHost ? String(info.platform) : "standalone",
        officeJsAvailable: hasHost,
      };

      console.log(`[Finster] Detection results:`, window.__FINSTER_HOST_INFO__);
      renderApp(isExcel ? "excel" : "powerpoint");
    });

    // Guard against Office.onReady never firing (script loaded but no host)
    setTimeout(() => {
      if (onReadyResolved) return;
      onReadyResolved = true;
      console.warn("[Finster] Office.onReady did not fire within 10s — falling back to standalone");
      markOfficeJsUnavailable();

      renderFactSetTaskpane();
    }, 10000);
  } else {
    markOfficeJsUnavailable();

    renderFactSetTaskpane();
  }
}

if (window.__officeJsReady === true) {
  boot();
} else if (window.__officeJsReady === false) {
  window.__finsterBoot = boot;
} else {
  boot();
}

if ((module as any).hot) {
  const renderCorrectApp = () => {
    const hostInfo = window.__FINSTER_HOST_INFO__;
    if (!hostInfo) {
      renderApp(null);
    } else if (hostInfo.isExcel) {
      const NextExcelApp = require("../excel/ExcelApp").default;
      root?.render(
        <ThemeProvider>
          <TooltipProvider>
            <NextExcelApp />
          </TooltipProvider>
        </ThemeProvider>
      );
    } else {
      const NextApp = require("../powerpoint/components/App").default;
      root?.render(
        <ThemeProvider>
          <TooltipProvider>
            <NextApp />
          </TooltipProvider>
        </ThemeProvider>
      );
    }
  };

  (module as any).hot.accept("../powerpoint/components/App", renderCorrectApp);
  (module as any).hot.accept("../excel/ExcelApp", renderCorrectApp);
}
